import { api, clearAuthToken, getAuthToken, setAuthToken } from './api';

/**
 * Reader Service — chamadas de API do leitor PWA (Sprint 1 rewrite, 2026-05-08)
 *
 * Resolve:
 *   - Login do Student (reader-auth)
 *   - Biblioteca (EbookAccess do Student)
 *   - Carregar ebook (presigned URL do ProductFile primário)
 *   - Salvar progresso (CFI + %)
 *
 * **Independente da área de membros** — todas as rotas chamadas aqui são
 * `/api/v1/reader-auth/*` e `/api/v1/reader/*` (não mais `/student-courses/*`).
 */

export interface Student {
  id: number;
  name: string;
  email: string;
  mustChangePassword: boolean;
}

export interface LibraryItem {
  accessId: number;
  productId: number;
  title: string;
  description: string | null;
  imageUrl: string | null;
  progress: number;
  completed: boolean;
  lastReadAt: string | null;
  slug?: string | null;
  /** MP1 — quantidade de quizzes ativos no produto (badge na biblioteca) */
  quizCount?: number;
  /** Bloco Rewards — quantidade de recompensas configuradas (badge na biblioteca) */
  rewardCount?: number;
}

export interface EbookData {
  productId: number;
  accessId: number;
  title: string;
  description: string | null;
  coverUrl: string | null;
  slug?: string | null;
  fileUrl: string;
  epubUrl?: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  progress: {
    progress: number;
    completed: boolean;
    cfi: string | null;
    lastReadAt: string | null;
    readingTimeSeconds: number;
  };
}

export interface ProgressUpdate {
  cfi?: string;
  progress?: number;
  completed?: boolean;
}

export interface UnlockedRewardPreview {
  id: number;
  title: string;
  rewardType: 'FILE' | 'EXTERNAL_LINK' | 'TEXT';
}

export const readerService = {
  /**
   * Login do Student via reader-auth (token JWT type=READER).
   */
  async login(email: string, password: string): Promise<{ student: Student; token: string }> {
    const { data } = await api.post('/api/v1/reader-auth/login', { email, password });
    const payload = data?.data || data;
    const token = payload?.token as string;
    const student = payload?.student as Student;

    if (!token || !student) {
      throw new Error('Resposta de login inválida');
    }

    setAuthToken(token);
    return { student, token };
  },

  async me(): Promise<Student> {
    if (!getAuthToken()) {
      throw new Error('Sessão não encontrada');
    }
    const { data } = await api.get('/api/v1/reader-auth/me');
    return data?.data || data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/v1/reader-auth/logout');
    } catch {
      // best effort
    }
    clearAuthToken();
  },

  /**
   * Biblioteca = EbookAccess ativos do Student logado.
   */
  async getLibrary(): Promise<LibraryItem[]> {
    const { data } = await api.get('/api/v1/reader/library');
    const list = (data?.data || []) as LibraryItem[];
    return list;
  },

  /**
   * Metadata + presigned URL do ebook (productId).
   */
  async getEbook(productId: number): Promise<EbookData> {
    const { data } = await api.get(`/api/v1/reader/products/${productId}/ebook`);
    return (data?.data || data) as EbookData;
  },

  /**
   * Mesma coisa via slug do produto (rota humanizada).
   */
  async getEbookBySlug(slug: string): Promise<EbookData> {
    const { data } = await api.get(
      `/api/v1/reader/products/slug/${encodeURIComponent(slug)}/ebook`,
    );
    return (data?.data || data) as EbookData;
  },

  /**
   * Salva progresso (debounce no caller). Retorna recompensas
   * desbloqueadas no movimento (vazio se nenhuma).
   */
  async updateProgress(
    productId: number,
    update: ProgressUpdate,
  ): Promise<UnlockedRewardPreview[]> {
    const { data } = await api.put(`/api/v1/reader/products/${productId}/progress`, update);
    const unlocked = data?.unlockedRewards;
    return Array.isArray(unlocked) ? (unlocked as UnlockedRewardPreview[]) : [];
  },
};
